import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import styles from './RechargeHistoryPage.module.css';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { useSystemLogin } from '../contexts/SystemLoginContext';
import config from '../config';
import { fetchWithAuth } from '../utils/api';

interface ShopAiPackageDefine {
  id: number;
  aiPackageType: number;
  aiPackageName: string;
  createdTime: string;
  lastUpdatedTime: string;
  monthlyUserProfileGenerationTokenTotalAmount: number;
  monthlyCompetitorVideoAnalysisTokenTotalAmount: number;
  monthlyVideoCutTokenTotalAmount: number;
  monthlyVideoDescGenerationTokenTotalAmount: number;
  monthlyVideoShootingScriptGenerationTokenTotalAmount: number;
  monthlyVideoGenerationTokenTotalAmount: number;
  monthlyCommentTokenTotalAmount: number;    
  universalTokenAmount: number;
  positionId: number;
  goodsId: number;
  price: number;
  currencyId: number;
  currencyCode: string;
  currencyName: string;
}

interface RechargeRecord {
  id: string;
  quantity: number;
  orderId: string;
  actualAmount: string;
  actualAmountStr: string;
  currency: string;
  deductionAmount: string;
  realDeductionAmount: string;
  time: string;
  generateRechargeStr: string;
  balance: string;
  beforeShopBalanceAmount: string;
  beforeShopBalanceAmountStr: string;
  afterShopBalanceAmount: string;
  afterShopBalanceAmountStr: string;
  status: number;
  statusStr: string;
  packageInfo?: ShopAiPackageDefine;
}

interface RechargeHistoryResponse {
  code: number;
  msg: string;
  data: RechargeRecord[];
}

const RechargeHistoryPage: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const systemLogin = useSystemLogin();
  
  // 使用 ref 来防止重复调用
  const initializationAttempted = useRef(false);
  const isFetching = useRef(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [records, setRecords] = useState<RechargeRecord[]>([]);
  const [pageNo, setPageNo] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const pageSize = 10;

  const fetchPackageInfo = useCallback(async (orderId: string): Promise<ShopAiPackageDefine | null> => {
    if (!orderId) {
      console.warn('Missing orderId when fetching package info');
      return null;
    }

    try {
      const response = await fetchWithAuth(
        `${config.API_BASE_URL}/app-api/order/queryAiPackageDefineByOrderId?orderId=${orderId}`,
        {
          method: 'GET',
          headers: {
            'Accept': 'application/json, text/plain, */*',
            'Accept-Language': 'en,zh-CN;q=0.9,zh;q=0.8',
            'Content-Type': 'application/json',
            'Origin': config.APP_BASE_URL,
            'Referer': `${config.APP_BASE_URL}/`
          }
        },
        navigate
      );

      if (response.code === 1 && response.data) {
        return response.data;
      }
      return null;
    } catch (error) {
      console.error('Error fetching package info:', error);
      return null;
    }
  }, [navigate]);

  const fetchRechargeHistory = useCallback(async (page: number, force: boolean = false) => {
    // 防止重复请求
    if (loading) {
      console.log('充值历史请求正在进行中，跳过重复请求');
      return;
    }

    // 防止重复调用的检查
    if (!force && isFetching.current) {
      console.log(`正在获取数据中，跳过重复请求`);
      return;
    }
    isFetching.current = true;
    setLoading(true);
    setError(null);
    try {
      const data = await fetchWithAuth(
        `${config.API_BASE_URL}/app-api/deductionReport/queryAiRechargeRecords?pageSize=${pageSize}&pageNo=${page}`,
        {
          method: 'POST',
          headers: {
            'Accept': 'application/json, text/plain, */*',
            'Accept-Language': 'en,zh-CN;q=0.9,zh;q=0.8',
            'Content-Type': 'application/json',
            'Origin': config.APP_BASE_URL,
            'Referer': `${config.APP_BASE_URL}/`
          },
          body: JSON.stringify({})
        },
        navigate
      );

      if (data.code === 1) {
        if (!Array.isArray(data.data) || data.data.length === 0) {
          setHasMore(false);
        }
        
        // Fetch package info for each record
        const recordsWithPackageInfo = await Promise.all(
          data.data.map(async (record: RechargeRecord) => {
            if (record.orderId) {
              const packageInfo = await fetchPackageInfo(record.orderId);
              return { ...record, packageInfo };
            }
            return record;
          })
        );

        if (page === 1) {
          setRecords(recordsWithPackageInfo);
        } else {
          setRecords(prev => [...prev, ...recordsWithPackageInfo]);
        }
      } else {
        setError(data.msg || '获取充值记录失败');
        setHasMore(false);
      }
    } catch (err) {
      console.error('Error fetching recharge history:', err);
      setError('网络错误，请重试');
    } finally {
      isFetching.current = false;
      setLoading(false);
    }
  }, [fetchPackageInfo, loading, navigate]);

  // 当登录状态改变时重置状态
  useEffect(() => {
    if (!systemLogin.isLoggedIn) {
      setRecords([]);
      setPageNo(1);
      setHasMore(true);
      initializationAttempted.current = false;
      setError(null);
    }
  }, [systemLogin.isLoggedIn]);

  useEffect(() => {
    if (systemLogin.isLoggedIn && !initializationAttempted.current) {
      initializationAttempted.current = true;
      fetchRechargeHistory(1);
    }
  }, [fetchRechargeHistory, systemLogin.isLoggedIn]);

  const loadMore = useCallback(() => {
    if (!loading && hasMore) {
      const nextPage = pageNo + 1;
      setPageNo(nextPage);
      fetchRechargeHistory(nextPage);
    }
  }, [loading, hasMore, pageNo, fetchRechargeHistory]);

  const formatDate = (dateString: string) => {
    return dateString;
  };

  const formatAmount = (amount: string) => {
    // 如果包含小数点，保留两位小数
    const parts = amount.split(' ');
    if (parts.length === 2) {
      const number = parseFloat(parts[0]);
      const currency = parts[1];
      // 转换货币标识
      const currencySymbol = currency === 'CNY' ? '¥' : currency === 'USD' ? '$' : currency;
      return `${currencySymbol} ${number.toFixed(2)}`;
    }
    return amount;
  };

  const getStatusColor = (status: number) => {
    switch (status) {
      case 1:
        return '#22c55e';
      case 0:
        return '#ff6b6b';
      default:
        return '#f59e0b';
    }
  };

  const getStatusText = (status: number) => {
    switch (status) {
      case 1:
        return '成功';
      case 0:
        return '失败';
      default:
        return '处理中';
    }
  };

  const renderPackageTokens = (packageInfo: ShopAiPackageDefine) => {
    if (!packageInfo) return null;

    switch (packageInfo.aiPackageType) {
      case 21: // Universal Token
        return (
          <div className={styles.tokenItem}>
            <span className={styles.tokenLabel}>通用令牌</span>
            <span className={styles.tokenValue}>{packageInfo.universalTokenAmount}</span>
          </div>
        );
      case 22: // Video Cut
        return (
          <div className={styles.tokenItem}>
            <span className={styles.tokenLabel}>视频剪辑令牌</span>
            <span className={styles.tokenValue}>{packageInfo.monthlyVideoCutTokenTotalAmount}</span>
          </div>
        );
      case 23: // Video Description
        return (
          <div className={styles.tokenItem}>
            <span className={styles.tokenLabel}>视频描述令牌</span>
            <span className={styles.tokenValue}>{packageInfo.monthlyVideoDescGenerationTokenTotalAmount}</span>
          </div>
        );
      default:
        // Membership package with multiple token types
        return (
          <div className={styles.tokenList}>
            {packageInfo.monthlyUserProfileGenerationTokenTotalAmount > 0 && (
              <div className={styles.tokenItem}>
                <span className={styles.tokenLabel}>用户档案生成</span>
                <span className={styles.tokenValue}>{packageInfo.monthlyUserProfileGenerationTokenTotalAmount}</span>
              </div>
            )}
            {packageInfo.monthlyCompetitorVideoAnalysisTokenTotalAmount > 0 && (
              <div className={styles.tokenItem}>
                <span className={styles.tokenLabel}>竞品视频分析</span>
                <span className={styles.tokenValue}>{packageInfo.monthlyCompetitorVideoAnalysisTokenTotalAmount}</span>
              </div>
            )}
            {packageInfo.monthlyVideoCutTokenTotalAmount > 0 && (
              <div className={styles.tokenItem}>
                <span className={styles.tokenLabel}>视频剪辑</span>
                <span className={styles.tokenValue}>{packageInfo.monthlyVideoCutTokenTotalAmount}</span>
              </div>
            )}
            {packageInfo.monthlyVideoDescGenerationTokenTotalAmount > 0 && (
              <div className={styles.tokenItem}>
                <span className={styles.tokenLabel}>视频描述生成</span>
                <span className={styles.tokenValue}>{packageInfo.monthlyVideoDescGenerationTokenTotalAmount}</span>
              </div>
            )}
            {packageInfo.monthlyVideoShootingScriptGenerationTokenTotalAmount > 0 && (
              <div className={styles.tokenItem}>
                <span className={styles.tokenLabel}>视频脚本生成</span>
                <span className={styles.tokenValue}>{packageInfo.monthlyVideoShootingScriptGenerationTokenTotalAmount}</span>
              </div>
            )}
            {packageInfo.monthlyVideoGenerationTokenTotalAmount > 0 && (
              <div className={styles.tokenItem}>
                <span className={styles.tokenLabel}>视频生成</span>
                <span className={styles.tokenValue}>{packageInfo.monthlyVideoGenerationTokenTotalAmount}</span>
              </div>
            )}
            {packageInfo.monthlyCommentTokenTotalAmount > 0 && (
              <div className={styles.tokenItem}>
                <span className={styles.tokenLabel}>评论生成</span>
                <span className={styles.tokenValue}>{packageInfo.monthlyCommentTokenTotalAmount}</span>
              </div>
            )}
          </div>
        );
    }
  };

  const handleBack = () => {
    navigate('/recharge');
  };

  if (!systemLogin.isLoggedIn) {
    return (
      <div className={styles.container}>
        <Header />
        <div className={styles.content}>
          <div className={styles.loginPrompt}>
            <h2>请先登录</h2>
            <p>登录后即可查看充值记录</p>
            <button onClick={() => navigate('/login')} className={styles.loginButton}>
              前往登录
            </button>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <div className={styles.content}>
        <header className={styles.header}>
          <button onClick={handleBack} className={styles.backButton}>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" className={styles.backIcon}>
              <path d="M19 12H5M12 19l-7-7 7-7" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" stroke="currentColor"/>
            </svg>
            返回
          </button>
          <h1 className={styles.title}>充值历史</h1>
        </header>

        <main className={styles.main}>
          {error && (
            <div className={styles.errorMessage}>
              <span>{error}</span>
              <button className={styles.retryButton} onClick={() => fetchRechargeHistory(1)}>
                重试
              </button>
            </div>
          )}

          <div className={styles.records}>
            {records.length === 0 && !loading && !error ? (
              <div className={styles.noRecords}>
                暂无充值记录
              </div>
            ) : (
              <>
                {records.map((record) => (
                  <div key={record.id} className={styles.recordItem}>
                    <div className={styles.recordHeader}>
                      <span className={styles.recordTime}>{formatDate(record.time)}</span>
                      <span className={styles.recordStatus} style={{ color: getStatusColor(record.status) }}>
                        {getStatusText(record.status)}
                      </span>
                    </div>
                    <div className={styles.recordAmount}>
                      <span className={styles.amountLabel}>充值金额</span>
                      <span className={styles.amountValue}>{formatAmount(record.actualAmountStr || record.actualAmount)}</span>
                    </div>
                    {record.packageInfo && (
                      <div className={styles.recordPackage}>
                        <div className={styles.packageName}>{record.packageInfo.aiPackageName}</div>
                        <div className={styles.packageTokens}>
                          {renderPackageTokens(record.packageInfo)}
                        </div>
                      </div>
                    )}
                  </div>
                ))}
                
                {records.length > 0 && (
                  <button 
                    className={styles.loadMoreButton} 
                    onClick={loadMore}
                    disabled={loading || !hasMore}
                  >
                    {loading ? '加载中...' : hasMore ? '加载更多' : '没有更多数据'}
                  </button>
                )}
                
                {loading && (
                  <div className={styles.loadingIndicator}>
                    <div className={styles.loadingSpinner} />
                  </div>
                )}
              </>
            )}
          </div>
        </main>
      </div>
      
      <Footer />
    </div>
  );
};

export default RechargeHistoryPage;